// src/theme/wellz.ts
import { createTheme } from "@mui/material";
import { ptBR } from "@mui/material/locale";

// src/theme/palette.ts
var palette = {
  primary: {
    light: "#710252",
    main: "#710252",
    dark: "#710252"
  },
  secondary: {
    light: "#F4D3ED",
    main: "#F4D3ED",
    dark: "#F4D3ED",
    contrastText: "#231B22"
  },
  error: {
    light: "#CA4808",
    main: "#CA4808",
    dark: "#CA4808"
  },
  warning: {
    light: "#FF956B",
    main: "#FF956B",
    dark: "#FF956B",
    contrastText: "#fff"
  },
  info: {
    light: "#BA95BA",
    main: "#BA95BA",
    dark: "#BA95BA",
    contrastText: "#fff"
  },
  success: {
    light: "#1D856C",
    main: "#1D856C",
    dark: "#1D856C",
    contrastText: "#fff"
  },
  common: {
    black: "#231B22"
  },
  text: {
    primary: "#231B22",
    secondary: "#6B6B78",
    disabled: "#D7D7E0"
  },
  divider: "#D7D7E0",
  background: {
    paper: "#F9F6F1"
  },
  action: {
    disabled: "#D7D7E0",
    disabledBackground: "#F5F5FA"
  }
};

// src/theme/overrides/button.ts
var buttonOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "24px",
        fontWeight: 500,
        textTransform: "none"
      }
    },
    variants: [
      {
        props: { size: "large" },
        style: {
          height: "42px",
          padding: "8px 22px"
        }
      },
      {
        props: { size: "medium" },
        style: {
          height: "36px",
          padding: "6px 16px"
        }
      },
      {
        props: { size: "small" },
        style: {
          height: "30px",
          padding: "4px 10px"
        }
      }
    ]
  }
};

// src/theme/overrides/input.ts
var outlinedInputOverrides = {
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        fontSize: "0.8750em"
      }
    }
  }
};

// src/theme/overrides/select.ts
var selectOverrides = {
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 8
      }
    }
  }
};
var menuItemOverrides = {
  MuiMenuItem: {
    styleOverrides: {
      root: {
        background: "#fff",
        "&:hover": {
          background: "rgba(107, 107, 120, 0.04)"
        },
        "&.Mui-selected": {
          background: "rgba(113, 2, 82, 0.04)"
        },
        "&.Mui-selected:hover": {
          background: "rgba(107, 107, 120, 0.04)"
        }
      }
    }
  }
};

// src/theme/overrides/tooltip.ts
var tooltipOverrides = {
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: "#231B22",
        color: "#fff",
        height: "36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      },
      arrow: {
        color: "#231B22"
      }
    }
  }
};

// src/theme/overrides/alert.ts
var alertOverrides = {
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8
      }
    },
    variants: [
      {
        props: { variant: "filled", severity: "info" },
        style: {
          backgroundColor: "#BA95BA",
          color: "#fff"
        }
      },
      {
        props: { variant: "filled", severity: "warning" },
        style: {
          backgroundColor: "#FF956B",
          color: "#fff"
        }
      },
      {
        props: { variant: "filled", severity: "error" },
        style: {
          backgroundColor: "#CA4808",
          color: "#fff"
        }
      }
    ]
  }
};

// src/theme/overrides/link.ts
var linkOverrides = {
  MuiLink: {
    styleOverrides: {
      root: {
        fontFamily: "Rubik, sans-serif",
        fontWeight: 500,
        lineHeight: "143%",
        letterSpacing: "0.17px",
        outlineColor: "#BA95BA"
      }
    }
  }
};

// src/theme/overrides/linearProgress.ts
var linearProgressOverrides = {
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: "4px"
      }
    }
  }
};

// src/theme/overrides/snackbar.ts
var snackbarOverrides = {
  MuiSnackbar: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
        fontFamily: "Rubik, sans-serif",
        fontWeight: 400,
        letterSpacing: "0.17px",
        lineHeight: "143%"
      }
    }
  }
};

// src/theme/overrides/textfield.ts
var textfieldOverrides = {
  MuiTextField: {
    styleOverrides: {
      root: {
        "> div": {
          borderRadius: "8px"
        }
      }
    }
  }
};

// src/theme/overrides/accordion.ts
var accordionOverrides = {
  MuiAccordion: {
    styleOverrides: {
      root: {
        background: "#ffffff"
      }
    }
  }
};

// src/theme/overrides/dialog.ts
var dialogOverrides = {
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        background: "#fff"
      }
    }
  }
};
var dialogTitleOverrides = {
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        textAlign: "center",
        marginTop: 40,
        marginBottom: 24
      }
    }
  }
};
var dialogContentOverrides = {
  MuiDialogContent: {
    styleOverrides: {
      root: {
        textAlign: "center"
      }
    }
  }
};
var dialogActionsOverrides = {
  MuiDialogActions: {
    styleOverrides: {
      root: {
        marginTop: 12,
        marginBottom: 32,
        display: "flex",
        justifyContent: "center",
        gap: 16
      }
    }
  }
};

// src/theme/wellz.ts
var muiTheme = createTheme(
  {
    palette
  },
  ptBR
);
var wellzTheme = createTheme(
  {
    ...muiTheme,
    typography: {
      fontFamily: ["Rubik", "sans-serif"].join(",")
    },
    components: {
      ...buttonOverrides,
      ...outlinedInputOverrides,
      ...selectOverrides,
      ...menuItemOverrides,
      ...tooltipOverrides,
      ...alertOverrides,
      ...dialogOverrides,
      ...dialogTitleOverrides,
      ...dialogContentOverrides,
      ...dialogActionsOverrides,
      ...linkOverrides,
      ...linearProgressOverrides,
      ...snackbarOverrides,
      ...textfieldOverrides,
      ...accordionOverrides
    }
  },
  {
    palette: {
      tangerine: muiTheme.palette.augmentColor({
        color: {
          main: "#FF956B"
        },
        name: "tangerine"
      }),
      mellow: muiTheme.palette.augmentColor({
        color: {
          main: "#FFC382"
        },
        name: "mellow"
      }),
      bubbles: muiTheme.palette.augmentColor({
        color: {
          main: "#92CCEA"
        },
        name: "bubbles"
      }),
      peach: muiTheme.palette.augmentColor({
        color: {
          main: "#FFF4E1"
        },
        name: "peach"
      }),
      icons: muiTheme.palette.augmentColor({
        color: {
          main: "#9898A6"
        },
        name: "icons"
      }),
      linesAndBorders: muiTheme.palette.augmentColor({
        color: {
          main: "#D7D7E0"
        },
        name: "linesAndBorders"
      })
    }
  }
);
export {
  wellzTheme
};
